import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Hand, Loader2, Minus, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { CharCounter } from '@/components/ui/char-counter';
import { cn } from '@/lib/utils';
import whatsappSettingsService, {
  HANDOFF_LIMITS,
  type HandoffSettings,
  type WhatsappSettings,
} from '@/services/whatsappSettingsService';
import voiceService from '@/services/voiceService';
import { withDeadline } from '@/lib/requestDeadline';
import {
  HANDOFF_BUTTON_LABELS,
  HANDOFF_FIELD_NAMES,
  changedHandoffFields,
  defaultBusyMessage,
  draftFrom,
  handoffInheritance,
  isDraftDirty,
  overriddenFields,
  stepMinutes,
  validateDraft,
  type HandoffDraft,
  type HandoffInheritance,
  type Salutation,
} from './handoffSettings';

interface HandoffSettingsCardProps {
  /** Agent number whose settings are shown; null = the company-wide defaults. */
  agentNumber: string | null;
  /** Display name of that agent for the „geerbt“ hint. */
  agentLabel?: string | null;
}

type LoadState = 'loading' | 'ready' | 'unsupported' | 'error';

const errorText = (err: unknown, fallback: string) => (err instanceof Error && err.message ? err.message : fallback);

/** The settings block the server stored — or an error when it did not return one. */
function handoffOf(response: WhatsappSettings): HandoffSettings {
  if (!response?.success) throw new Error(response?.error || 'Speichern fehlgeschlagen.');
  if (!response.handoff) throw new Error('Der Server kennt diese Einstellung noch nicht.');
  return response.handoff;
}

function MinuteStepper({
  id,
  label,
  hint,
  value,
  range,
  error,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  range: { min: number; max: number };
  error?: string;
  onChange: (value: string) => void;
}) {
  const n = Number(value);
  const valid = /^\d+$/.test(value.trim());
  const atMin = valid && n <= range.min;
  const atMax = valid && n >= range.max;
  const errorId = `${id}-error`;
  // aria-disabled, not disabled: stepping to the bound must not drop keyboard focus.
  const stepButton =
    'grid h-9 w-9 place-items-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 aria-disabled:cursor-default aria-disabled:opacity-40';
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1 space-y-0.5">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        <p className="text-xs leading-5 text-muted-foreground">{hint}</p>
        {error && (
          <p id={errorId} className="text-xs font-medium text-destructive-strong">
            {error}
          </p>
        )}
      </div>
      <div
        className={cn(
          'inline-flex shrink-0 items-center self-start rounded-lg border bg-background',
          error ? 'border-destructive-strong' : 'border-border',
        )}
      >
        <button
          type="button"
          className={cn(stepButton, 'rounded-l-lg')}
          aria-label={`${label}: eine Minute weniger`}
          aria-disabled={atMin}
          onClick={() => {
            if (!atMin) onChange(stepMinutes(value, -1, range));
          }}
        >
          <Minus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <input
          id={id}
          inputMode="numeric"
          value={value}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, '').slice(0, 2))}
          className="h-9 w-10 bg-transparent text-center text-sm font-semibold tabular-nums focus-visible:outline-none"
        />
        <span className="pr-1 text-xs text-muted-foreground">min</span>
        <button
          type="button"
          className={cn(stepButton, 'rounded-r-lg')}
          aria-label={`${label}: eine Minute mehr`}
          aria-disabled={atMax}
          onClick={() => {
            if (!atMax) onChange(stepMinutes(value, 1, range));
          }}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/**
 * „Übergabe an Mitarbeiter“ in the WhatsApp tab of the agent page: the
 * switch in the header saves right away (optimistic, rolled back on error,
 * like the cross-selling switch); the details below are a small form with its
 * own „Speichern“.
 */
export function HandoffSettingsCard({ agentNumber, agentLabel }: HandoffSettingsCardProps) {
  const baseId = useId();
  const [state, setState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState<HandoffSettings | null>(null);
  const [draft, setDraft] = useState<HandoffDraft | null>(null);
  /** Per field: company value? Only for an agent number (null = company scope). */
  const [inheritance, setInheritance] = useState<HandoffInheritance | null>(null);
  const [salutation, setSalutation] = useState<Salutation>('du');
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const load = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const [settings, voice] = await Promise.all([
          withDeadline(whatsappSettingsService.getSettings(agentNumber)),
          // Only for the Du/Sie of the standard text — optional.
          withDeadline(voiceService.getSettings(agentNumber)).catch(() => null),
        ]);
        if (cancelled) return;
        setSalutation(voice?.salutation === 'sie' ? 'sie' : 'du');
        if (!settings?.success) throw new Error(settings?.error || 'Einstellungen konnten nicht geladen werden.');
        if (!settings.handoff) {
          setState('unsupported');
          return;
        }
        setSaved(settings.handoff);
        setDraft(draftFrom(settings.handoff));
        setInheritance(agentNumber ? handoffInheritance(settings.inherited) : null);
        setLoadError(null);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setLoadError(errorText(err, 'Einstellungen konnten nicht geladen werden.'));
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentNumber, attempt]);

  const errors = useMemo(() => (draft ? validateDraft(draft) : {}), [draft]);
  const hasErrors = Object.keys(errors).length > 0;
  const dirty = Boolean(draft && saved && isDraftDirty(draft, saved));
  const standardText = defaultBusyMessage(salutation);

  const applyServer = (response: WhatsappSettings) => {
    const stored = handoffOf(response);
    setSaved(stored);
    setInheritance(agentNumber ? handoffInheritance(response.inherited) : null);
    return stored;
  };

  // One request at a time (each carries the other's `enabled`). The controls
  // stay focusable (aria-disabled) and every request has a deadline, so a hung
  // PUT can neither steal keyboard focus nor lock the card for good.
  const busy = toggling || saving || resetting;
  const canSave = dirty && !hasErrors && !busy;

  const toggleEnabled = async (next: boolean) => {
    if (!saved || busy) return;
    const previous = saved;
    setSaved({ ...saved, enabled: next });
    setToggling(true);
    try {
      // Only the switch: for an agent number every field sent becomes its own
      // value and would stop following the company.
      const response = await withDeadline(whatsappSettingsService.updateSettings({ handoff: { enabled: next } }, agentNumber));
      const stored = applyServer(response);
      toast.success(stored.enabled ? 'Übergabe an Mitarbeiter aktiviert.' : 'Übergabe an Mitarbeiter deaktiviert.');
    } catch (err) {
      setSaved(previous);
      toast.error(errorText(err, 'Konnte Einstellung nicht speichern.'));
    } finally {
      setToggling(false);
    }
  };

  const save = async () => {
    if (!draft || !saved || !canSave) return;
    setSaving(true);
    try {
      const response = await withDeadline(
        whatsappSettingsService.updateSettings({ handoff: changedHandoffFields(draft, saved) }, agentNumber),
      );
      const stored = applyServer(response);
      setDraft(draftFrom(stored));
      toast.success('Übergabe-Einstellungen gespeichert.');
    } catch (err) {
      toast.error(errorText(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  /** The agent follows the company again in every handoff field. */
  const resetToCompany = async () => {
    if (!agentNumber || busy) return;
    setResetting(true);
    try {
      const response = await withDeadline(whatsappSettingsService.updateSettings({ reset_fields: ['handoff'] }, agentNumber));
      const stored = applyServer(response);
      setDraft(draftFrom(stored));
      toast.success('Unternehmensstandard übernommen.');
    } catch (err) {
      toast.error(errorText(err, 'Konnte den Unternehmensstandard nicht übernehmen.'));
    } finally {
      setResetting(false);
    }
  };

  const update = (patch: Partial<HandoffDraft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const overridden = inheritance ? overriddenFields(inheritance) : [];
  const agentName = agentLabel || 'diesen Agenten';
  const enabled = saved?.enabled === true;
  const previewText = draft?.busyMessage.trim() || standardText;

  return (
    <Card className="overflow-hidden rounded-[28px] border-border shadow-sm">
      <CardHeader className="space-y-2 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <CardTitle className="flex items-center gap-2 pt-1 text-base">
            <Hand className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
            Übergabe an Mitarbeiter
          </CardTitle>
          {state === 'ready' && (
            <div className="flex shrink-0 items-center gap-2 pt-0.5">
              <Switch
                id={`${baseId}-enabled`}
                checked={enabled}
                onCheckedChange={toggleEnabled}
                aria-disabled={busy}
                aria-busy={toggling}
                aria-label="Übergabe an Mitarbeiter"
                className="data-[state=checked]:bg-brand aria-disabled:cursor-wait aria-disabled:opacity-60"
              />
              <Label htmlFor={`${baseId}-enabled`} className="min-w-[3rem] text-sm font-normal">
                {enabled ? 'Aktiv' : 'Aus'}
              </Label>
            </div>
          )}
        </div>
        <p className="max-w-prose text-sm text-muted-foreground">
          Möchte ein Kunde auf WhatsApp mit einem Menschen schreiben, pausiert der Agent und in der Web-App klingelt
          die Glocke – ihr antwortet direkt in „Verläufe“. Ist gerade niemand frei oder antwortet keiner rechtzeitig,
          bietet der Agent an, dass sich jemand meldet, und hilft bis dahin selbst weiter.
        </p>
        {inheritance && state === 'ready' && overridden.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Standard des Unternehmens — gilt für alle Agenten, bis du ihn hier für{' '}
            <span className="font-medium">{agentName}</span> überschreibst.
          </p>
        )}
        {inheritance && state === 'ready' && overridden.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <p>
              Eigene Werte für <span className="font-medium">{agentName}</span>:{' '}
              {overridden.map((field) => HANDOFF_FIELD_NAMES[field]).join(', ')}. Alles andere kommt vom Unternehmen.
            </p>
            <button
              type="button"
              onClick={resetToCompany}
              aria-disabled={busy}
              aria-busy={resetting}
              className="rounded font-medium text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 aria-disabled:cursor-wait aria-disabled:opacity-60"
            >
              Unternehmensstandard übernehmen
            </button>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
        {state === 'loading' && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Lädt…
          </p>
        )}

        {state === 'unsupported' && (
          <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
            Diese Funktion wird gerade eingeführt und ist für dein Konto noch nicht verfügbar.
          </p>
        )}

        {state === 'error' && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 px-3 py-2 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive-strong" aria-hidden="true" />
            <span className="flex-1">{loadError}</span>
            <Button variant="outline" size="sm" onClick={load}>
              Erneut laden
            </Button>
          </div>
        )}

        {state === 'ready' && draft && (
          <>
            {!enabled && (
              <p className="text-xs text-muted-foreground">
                Die Einstellungen unten gelten, sobald die Übergabe eingeschaltet ist.
              </p>
            )}
            <div className="flex items-start justify-between gap-4 rounded-xl border border-border px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor={`${baseId}-offer`} className="text-sm font-medium">
                  Bei Problemen aktiv anbieten
                </Label>
                <p className="text-xs leading-5 text-muted-foreground">
                  Kommt der Agent nicht weiter (z. B. bei einer Reklamation), fragt er, ob er an euch übergeben soll.
                </p>
              </div>
              <Switch
                id={`${baseId}-offer`}
                checked={draft.offerOnProblems}
                onCheckedChange={(checked) => update({ offerOnProblems: checked })}
                className="mt-0.5 data-[state=checked]:bg-brand"
              />
            </div>

            <MinuteStepper
              id={`${baseId}-wait`}
              label="Wartezeit, bis der Agent wieder einspringt"
              hint="Antwortet in dieser Zeit niemand, übernimmt der Agent wieder und schreibt dem Kunden."
              value={draft.waitMinutes}
              range={HANDOFF_LIMITS.waitMinutes}
              error={errors.waitMinutes}
              onChange={(value) => update({ waitMinutes: value })}
            />
            <MinuteStepper
              id={`${baseId}-idle`}
              label="Rückgabe bei Ruhe im Chat"
              hint="Schreibt nach eurer Übernahme niemand mehr, bekommt der Agent das Gespräch still zurück."
              value={draft.idleMinutes}
              range={HANDOFF_LIMITS.idleMinutes}
              error={errors.idleMinutes}
              onChange={(value) => update({ idleMinutes: value })}
            />

            <div className="space-y-2">
              <Label htmlFor={`${baseId}-busy`} className="text-sm font-medium">
                Nachricht, wenn gerade niemand frei ist
              </Label>
              <Textarea
                id={`${baseId}-busy`}
                value={draft.busyMessage}
                placeholder={standardText}
                maxLength={HANDOFF_LIMITS.busyMessageMax}
                rows={4}
                aria-invalid={Boolean(errors.busyMessage)}
                onChange={(e) => update({ busyMessage: e.target.value })}
                className="bg-background"
              />
              <CharCounter
                value={draft.busyMessage}
                max={HANDOFF_LIMITS.busyMessageMax}
                hint={`Leer lassen für den Standardtext (${salutation === 'sie' ? 'Sie' : 'Du'}-Form wie eingestellt).`}
              />
              {errors.busyMessage && (
                <p className="text-xs font-medium text-destructive-strong">{errors.busyMessage}</p>
              )}
              <div className="rounded-xl bg-muted/60 p-3" role="group" aria-label="Vorschau in WhatsApp">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  So sieht es der Kunde
                </p>
                <div className="max-w-md whitespace-pre-line rounded-2xl rounded-bl-md border border-border bg-background px-3 py-2 text-sm">
                  {previewText}
                </div>
                <div className="mt-2 flex max-w-md flex-wrap gap-1.5">
                  {HANDOFF_BUTTON_LABELS.map((label) => (
                    <span
                      key={label}
                      className="flex-1 rounded-xl border border-border bg-background px-3 py-1.5 text-center text-sm font-medium text-brand"
                    >
                      {label}
                    </span>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Die Knöpfe „Ja, bitte melden“ und „Nein, danke“ hängt das System automatisch an. „Ja“ hinterlegt
                  einen Rückmeldewunsch, der oben in der Glocke und in „Verläufe“ steht, bis sich jemand meldet.
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={save}
                aria-disabled={!canSave}
                aria-busy={saving}
                className="min-w-[140px] bg-brand text-brand-foreground hover:bg-brand-hover aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-brand"
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                Speichern
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default HandoffSettingsCard;
